import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import Footer from './Footer'

interface Club {
  id: number
  name: string
  created_at: string
}

interface ClubAge {
  total_days: number
  years: number
  days: number
}

interface PopularGenre {
  genre: string
  count: number
}

interface BooksPerMonthDataPoint {
  month: string
  date: string
  books_completed: number
  cumulative_books: number
  books_per_month_rate: number
}

interface TopRatedBook {
  book_pick_id: number
  book_id: number
  title: string
  author: string
  cover_url?: string
  genre?: string
  average_rating: number
  review_count: number
  completed_at: string
}

interface ReadingPace {
  average_days: number
  total_books: number
}

interface FastestRead {
  title: string
  author: string
  days: number
  completed_at: string
}

interface GenreDistribution {
  genre: string
  count: number
  percentage: number
}

interface Stats {
  club_age: ClubAge | null
  books_read_count: number
  most_popular_genre: PopularGenre | null
  books_per_month: BooksPerMonthDataPoint[]
  top_rated_books: TopRatedBook[]
  reading_pace: ReadingPace | null
  fastest_reads: { fastest: FastestRead[], slowest: FastestRead[] }
  genre_distribution: GenreDistribution[]
  current_streak: number
}

interface StatsData {
  club: Club
  stats: Stats
}

const GENRE_COLORS = [
  'from-rose-400 to-rose-500',
  'from-blue-400 to-blue-500',
  'from-amber-400 to-amber-500',
  'from-purple-400 to-purple-500',
  'from-green-400 to-green-500',
  'from-pink-400 to-pink-500',
  'from-cyan-400 to-cyan-500',
  'from-indigo-400 to-indigo-500',
  'from-orange-400 to-orange-500',
  'from-teal-400 to-teal-500'
]

export default function ClubStats() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<StatsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchStats()
  }, [id])

  const fetchStats = async () => {
    try {
      const response = await fetch(`/api/v1/clubs/${id}/stats`)
      if (response.ok) {
        const statsData = await response.json()
        setData(statsData)
      } else {
        setError('Failed to load stats')
      }
    } catch (err) {
      setError('Failed to load stats')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error || 'Failed to load stats'}</p>
          </div>
        </div>
      </div>
    )
  }

  const { club, stats } = data

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/clubs/${id}`)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
                  {club.name} Stats
                </h1>
                <p className="text-xs text-gray-600">Reading insights and analytics</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Books Read */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-sm font-medium opacity-90">Books Read</span>
              </div>
              <p className="text-5xl font-bold mb-1">{stats.books_read_count}</p>
              <p className="text-sm opacity-75">and counting!</p>
            </div>
          </div>

          {/* Reading Pace */}
          <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium opacity-90">Average Pace</span>
              </div>
              {stats.reading_pace ? (
                <>
                  <p className="text-5xl font-bold mb-1">{Math.round(stats.reading_pace.average_days)}</p>
                  <p className="text-sm opacity-75">days per book</p>
                </>
              ) : (
                <>
                  <p className="text-5xl font-bold mb-1">-</p>
                  <p className="text-sm opacity-75">Not enough data</p>
                </>
              )}
            </div>
          </div>

          {/* Streak */}
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                </svg>
                <span className="text-sm font-medium opacity-90">Reading Streak</span>
              </div>
              <p className="text-5xl font-bold mb-1">{stats.current_streak}</p>
              <p className="text-sm opacity-75">month{stats.current_streak !== 1 ? 's' : ''} strong</p>
            </div>
          </div>

          {/* Club Age */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium opacity-90">Club Age</span>
              </div>
              {stats.club_age ? (
                <>
                  <p className="text-5xl font-bold mb-1">{stats.club_age.years}</p>
                  <p className="text-sm opacity-75">years, {stats.club_age.days} days</p>
                </>
              ) : (
                <>
                  <p className="text-5xl font-bold mb-1">0</p>
                  <p className="text-sm opacity-75">years</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Reading Rate Graph */}
            {stats.books_per_month.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Reading Rate</h3>
                    <p className="text-sm text-gray-600">Books per month over time</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.books_per_month}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      label={{ value: 'Books/Month', angle: -90, position: 'insideLeft', style: { fill: '#6b7280', fontSize: 12 } }}
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload as BooksPerMonthDataPoint
                          return (
                            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                              <p className="font-semibold text-gray-900 mb-1">{data.month}</p>
                              <p className="text-sm text-indigo-600 font-medium">Rate: {data.books_per_month_rate} books/month</p>
                              <p className="text-sm text-gray-600">Completed: {data.books_completed}</p>
                              <p className="text-sm text-gray-600">Total: {data.cumulative_books}</p>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="books_per_month_rate"
                      stroke="#6366f1"
                      strokeWidth={3}
                      dot={{ fill: '#6366f1', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Books per Month"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Fastest & Slowest Reads */}
            {stats.fastest_reads.fastest.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Fastest */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Speed Reads</h3>
                  </div>
                  <div className="space-y-3">
                    {stats.fastest_reads.fastest.map((book, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm truncate">{book.title}</p>
                          <p className="text-xs text-gray-600">by {book.author}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{book.days}</p>
                          <p className="text-xs text-gray-500">days</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Slowest */}
                {stats.fastest_reads.slowest.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-2 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Slow Burns</h3>
                    </div>
                    <div className="space-y-3">
                      {stats.fastest_reads.slowest.map((book, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 text-sm truncate">{book.title}</p>
                            <p className="text-xs text-gray-600">by {book.author}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-blue-600">{book.days}</p>
                            <p className="text-xs text-gray-500">days</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Top Rated Books */}
            {stats.top_rated_books.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-yellow-100 to-amber-100 rounded-lg">
                    <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Top Rated Books</h3>
                    <p className="text-sm text-gray-600">Club favorites</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {stats.top_rated_books.map((book, index) => (
                    <div key={book.book_pick_id} className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-yellow-300 transition-all">
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                        {index + 1}
                      </div>
                      {book.cover_url ? (
                        <img
                          src={book.cover_url}
                          alt={book.title}
                          className="w-12 h-18 object-cover rounded shadow-md flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-18 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-900 truncate">{book.title}</h4>
                        <p className="text-sm text-gray-600 truncate">by {book.author}</p>
                        {book.genre && (
                          <p className="text-xs text-gray-500 mt-1">{book.genre}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1">
                          <svg className="w-6 h-6 text-yellow-400 fill-current" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="font-bold text-xl text-gray-900">{book.average_rating.toFixed(1)}</span>
                        </div>
                        <span className="text-xs text-gray-500">({book.review_count})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Genre Bookshelf */}
            {stats.genre_distribution.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Genre Library</h3>
                    <p className="text-sm text-gray-600">Your reading diversity</p>
                  </div>
                </div>

                {/* Bookshelf Visualization */}
                <div className="mb-6">
                  <div className="flex items-end justify-center gap-1 h-48 bg-gradient-to-b from-gray-50 to-amber-50 rounded-lg p-4 border border-gray-200">
                    {stats.genre_distribution.slice(0, 10).map((genre, idx) => {
                      const maxHeight = Math.max(...stats.genre_distribution.slice(0, 10).map(g => g.count))
                      const heightPercentage = (genre.count / maxHeight) * 100
                      return (
                        <div
                          key={idx}
                          className="group relative flex-1 flex flex-col justify-end"
                        >
                          <div
                            className={`bg-gradient-to-t ${GENRE_COLORS[idx % GENRE_COLORS.length]} rounded-t shadow-md hover:shadow-lg transition-all cursor-pointer relative`}
                            style={{ height: `${heightPercentage}%`, minHeight: '40px' }}
                          >
                            <div className="absolute inset-x-0 top-1 h-px bg-white opacity-30"></div>
                            <div className="absolute inset-x-0 bottom-1 h-px bg-black opacity-20"></div>
                          </div>
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                              <p className="font-semibold">{genre.genre}</p>
                              <p className="text-gray-300">{genre.count} books ({genre.percentage}%)</p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="mt-2 h-2 bg-gradient-to-b from-amber-600 to-amber-700 rounded-sm shadow"></div>
                </div>

                {/* Genre List */}
                <div className="space-y-2">
                  {stats.genre_distribution.slice(0, 5).map((genre, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded bg-gradient-to-br ${GENRE_COLORS[idx % GENRE_COLORS.length]} flex-shrink-0`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-900 truncate">{genre.genre}</span>
                          <span className="text-xs text-gray-600 flex-shrink-0 ml-2">{genre.count} books</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full bg-gradient-to-r ${GENRE_COLORS[idx % GENRE_COLORS.length]}`}
                            style={{ width: `${genre.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Most Popular Genre */}
            {stats.most_popular_genre && (
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-xl p-6 text-white">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  <span className="text-sm font-medium opacity-90">Favorite Genre</span>
                </div>
                <p className="text-3xl font-bold mb-1">{stats.most_popular_genre.genre}</p>
                <p className="text-sm opacity-75">{stats.most_popular_genre.count} books and counting</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
